import React, { useEffect, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { locationsApi } from "../api";
import LocationFormModal from "../components/LocationFormModal";
import ConfirmDialog from "../components/ConfirmDialog";

export default function LocationsView() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const refresh = async () => {
    try {
      const data = await locationsApi.list();
      setLocations(data);
    } catch (e) {
      toast.error("Failed to load locations", { description: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleSave = async (data) => {
    const payload = {
      name: data.name,
      address: data.address,
      lat: +data.lat || 0,
      lng: +data.lng || 0,
      tax: +data.tax || 0,
      min_booking_days: +data.minBookingDays || 1,
    };
    try {
      if (data.id) {
        await locationsApi.update(data.id, payload);
        toast.success("Location updated");
      } else {
        await locationsApi.create(payload);
        toast.success("Location added");
      }
      setEditing(null);
      setAdding(false);
      refresh();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const handleDelete = async () => {
    try {
      await locationsApi.remove(confirmDel.id);
      toast.success(`Deleted "${confirmDel.name}"`);
      setConfirmDel(null);
      refresh();
    } catch (e) {
      toast.error("Delete failed", { description: e.message });
    }
  };

  // map snake_case → camelCase for the form
  const toFormShape = (l) => ({
    ...l,
    minBookingDays: l.min_booking_days,
  });

  return (
    <div className="section-card">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold">Locations</h2>
        <button className="pill-btn primary" onClick={() => setAdding(true)}>
          + Add Location
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <Loader2 className="animate-spin mr-2" size={18} /> Loading locations...
        </div>
      ) : (
        <div className="space-y-3">
          {locations.map((l) => (
            <div className="list-row" key={l.id}>
              <div className="location-pin">
                <MapPin size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold">{l.name}</div>
                <div className="text-sm muted">
                  {l.address} · {Number(l.lat).toFixed(4)}, {Number(l.lng).toFixed(4)} ·{" "}
                  <span className="text-red-strong">Tax {l.tax}%</span> ·{" "}
                  <span className="font-semibold text-gray-900">
                    Min {l.min_booking_days ?? 1} day{(l.min_booking_days ?? 1) === 1 ? "" : "s"}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button className="pill-btn blue" onClick={() => setEditing(toFormShape(l))}>
                  Edit
                </button>
                <button className="pill-btn gray" onClick={() => setConfirmDel(l)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {(adding || editing) && (
        <LocationFormModal
          initial={editing}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSave={handleSave}
        />
      )}
      {confirmDel && (
        <ConfirmDialog
          title="Delete location"
          message={`Are you sure you want to delete "${confirmDel.name}"?`}
          onCancel={() => setConfirmDel(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}
